/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface MedicalControlContactLogEntryProps {
  id: string;
  cardId: string;
  timestampId: string;
  personId: string;
  isSuccessful: boolean;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class MedicalControlContactLogEntryRecord extends Record<MedicalControlContactLogEntryProps>(
  {
    id: null as any,
    cardId: null as any,
    timestampId: null as any,
    personId: null as any,
    isSuccessful: null as any,
    companyId: null as any,
    dateLastUpdated: null as any,
    dateCreated: null as any,
    dateDeleted: null as any,
    lastUpdatedById: null as any,
  },
) {
  public static fromJS(json: {
    id: string;
    cardId: string;
    timestampId: string;
    personId: string;
    isSuccessful: boolean;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): MedicalControlContactLogEntryRecord {
    if (json == null) {
      throw new Error('MedicalControlContactLogEntryRecord.fromJS: parameter cannot be null');
    }
    return new MedicalControlContactLogEntryRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'MedicalControlContactLogEntryJson.id',
      ),
      cardId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'cardId', '00000000-0000-0000-0000-000000000000'),
        'MedicalControlContactLogEntryJson.cardId',
      ),
      timestampId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'timestampId', '00000000-0000-0000-0000-000000000000'),
        'MedicalControlContactLogEntryJson.timestampId',
      ),
      personId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'personId', '00000000-0000-0000-0000-000000000000'),
        'MedicalControlContactLogEntryJson.personId',
      ),
      isSuccessful: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isSuccessful', false),
        'MedicalControlContactLogEntryJson.isSuccessful',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'MedicalControlContactLogEntryJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    cardId: string;
    timestampId: string;
    personId: string;
    isSuccessful: boolean;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [MedicalControlContactLogEntryRecord, Error[]] {
    if (json == null) {
      throw new Error('MedicalControlContactLogEntryRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new MedicalControlContactLogEntryRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'MedicalControlContactLogEntryJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      cardId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'cardId', '00000000-0000-0000-0000-000000000000'),
            'MedicalControlContactLogEntryJson.cardId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      timestampId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'timestampId', '00000000-0000-0000-0000-000000000000'),
            'MedicalControlContactLogEntryJson.timestampId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      personId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'personId', '00000000-0000-0000-0000-000000000000'),
            'MedicalControlContactLogEntryJson.personId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isSuccessful: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isSuccessful', false),
            'MedicalControlContactLogEntryJson.isSuccessful',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'MedicalControlContactLogEntryJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: MedicalControlContactLogEntryProps) {
    super(props);
    if (props == null) {
      throw new Error('MedicalControlContactLogEntryRecord constructor parameter cannot be null');
    }
  }
}

export abstract class MedicalControlContactLogEntryViewModelBase
  implements MedicalControlContactLogEntryProps
{
  protected readonly _props: MedicalControlContactLogEntryProps;
  private _json?: MedicalControlContactLogEntryProps;

  get id(): string {
    return this._props.id;
  }
  get cardId(): string {
    return this._props.cardId;
  }
  get timestampId(): string {
    return this._props.timestampId;
  }
  get personId(): string {
    return this._props.personId;
  }
  get isSuccessful(): boolean {
    return this._props.isSuccessful;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: MedicalControlContactLogEntryProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        cardId: this._props.cardId,
        timestampId: this._props.timestampId,
        personId: this._props.personId,
        isSuccessful: this._props.isSuccessful,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllMedicalControlContactLogEntryEntities = (state: AppState) =>
  state.entityStores.medicalControlContactLogEntry.entities;

export const selectMedicalControlContactLogEntryById = (id: string) => (state: AppState) =>
  state.entityStores.medicalControlContactLogEntry.entities.get(id);

export const selectMedicalControlContactLogEntryMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.medicalControlContactLogEntry.meta.get(id);

export const selectMedicalControlContactLogEntryAndMetadataById = (id: string) =>
  createSelector(
    selectMedicalControlContactLogEntryById(id),
    selectMedicalControlContactLogEntryMetadataById(id),
    (medicalControlContactLogEntry, meta) => ({ medicalControlContactLogEntry, meta }),
  );

export function selectMedicalControlContactLogEntryEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllMedicalControlContactLogEntryEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, MedicalControlContactLogEntryRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectMedicalControlContactLogEntryEntitiesByCardId(cardId: string) {
  return createSelector(selectAllMedicalControlContactLogEntryEntities, (entities) =>
    entities.filter((e) => e.cardId === cardId).valueSeq(),
  );
}

export function selectMedicalControlContactLogEntryEntitiesByTimestampId(timestampId: string) {
  return createSelector(selectAllMedicalControlContactLogEntryEntities, (entities) =>
    entities.filter((e) => e.timestampId === timestampId).valueSeq(),
  );
}

export function selectMedicalControlContactLogEntryEntitiesByPersonId(personId: string) {
  return createSelector(selectAllMedicalControlContactLogEntryEntities, (entities) =>
    entities.filter((e) => e.personId === personId).valueSeq(),
  );
}

export function selectMedicalControlContactLogEntryEntitiesByIsSuccessful(isSuccessful: boolean) {
  return createSelector(selectAllMedicalControlContactLogEntryEntities, (entities) =>
    entities.filter((e) => e.isSuccessful === isSuccessful).valueSeq(),
  );
}

export function selectMedicalControlContactLogEntryEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllMedicalControlContactLogEntryEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
